import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, Clock, User, ArrowRight } from "lucide-react";
import { BlogPost } from "../data/blogPosts";

interface BlogCardProps {
  post: BlogPost;
  variant?: "featured" | "compact";
  animationDelay?: number;
}

const BlogCard = ({ post, variant = "compact", animationDelay = 0 }: BlogCardProps) => {
  const isFeatured = variant === "featured";
  
  return (
    <Link to={`/blog/${post.id}`} className="block h-full">
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay }}
        className={`${
          isFeatured 
            ? "bg-slate-800/50 rounded-2xl p-8 hover:bg-slate-800/70" 
            : "bg-slate-800/50 rounded-xl p-6 hover:bg-slate-800/70"
        } transition-colors group cursor-pointer h-full`}
      >
        {/* Meta information */}
        <div className={`flex items-center space-x-${isFeatured ? "4" : "3"} mb-${isFeatured ? "4" : "3"} text-${isFeatured ? "sm" : "xs"} text-slate-400`}>
          <div className="flex items-center space-x-1">
            <Calendar className={`w-${isFeatured ? "4" : "3"} h-${isFeatured ? "4" : "3"}`} />
            <span>{new Date(post.date).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Clock className={`w-${isFeatured ? "4" : "3"} h-${isFeatured ? "4" : "3"}`} />
            <span>{post.readTime}</span>
          </div>
          {isFeatured && (
            <div className="flex items-center space-x-1">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
          )}
        </div>
        
        {/* Title */}
        <h4 className={`${
          isFeatured 
            ? "text-xl font-bold mb-3 group-hover:text-teal-400 transition-colors" 
            : "text-lg font-bold mb-2 group-hover:text-teal-400 transition-colors line-clamp-2"
        }`}>
          {post.title}
        </h4>
        
        {/* Excerpt */}
        <p className={`text-slate-300 mb-4 ${
          isFeatured 
            ? "line-clamp-3" 
            : "text-sm line-clamp-2"
        }`}>
          {post.excerpt}
        </p>
        
        {/* Tags */}
        <div className={`flex flex-wrap gap-${isFeatured ? "2" : "1"} mb-${isFeatured ? "6" : "4"}`}>
          {isFeatured ? (
            post.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-slate-700 text-slate-300 text-xs rounded-full"
              >
                {tag}
              </span>
            ))
          ) : (
            <>
              {post.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded"
                >
                  {tag}
                </span>
              ))}
              {post.tags.length > 2 && (
                <span className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
                  +{post.tags.length - 2}
                </span>
              )}
            </>
          )}
        </div>
        
        {/* Read more link */}
        <div className="inline-flex items-center space-x-2 text-teal-400 hover:text-teal-300 transition-colors group">
          <span className={isFeatured ? "" : "text-sm"}>Read more</span>
          <ArrowRight className={`w-${isFeatured ? "4" : "3"} h-${isFeatured ? "4" : "3"} group-hover:translate-x-1 transition-transform`} />
        </div>
      </motion.article>
    </Link>
  );
};

export default BlogCard;
